import React, { useEffect, Suspense, lazy } from 'react';
import { Helmet } from 'react-helmet';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import LoadingSpinner from './components/LoadingSpinner';
import { trackGAPage } from './services/ga4Client';
import OutageBanner from './components/OutageBanner';
import { config } from './config/config';
import { PWAInstallProvider } from './context/PWAInstallContext.tsx';

const Landing = lazy(() => import('./pages/Landing2.tsx'));
const AppWithProviders = lazy(() => import('./AppWithProviders'));
const isLandingPath = (pathname) => pathname === '/';

// Track page views on SPA route changes
function GA4RouteTracker() {
  const location = useLocation();

  useEffect(() => {
    trackGAPage(window.location.origin + location.pathname + location.search);

    const params = new URLSearchParams(location.search);
    if (params.get('ref') === 'push') {
      const captureKey = `push_ref_captured:${location.pathname}${location.search}`;
      if (!sessionStorage.getItem(captureKey)) {
        import('posthog-js').then((module) => {
          module.default.capture('push_notification_clicked');
        }).catch(() => {
          // no-op
        });
        sessionStorage.setItem(captureKey, '1');
      }
    }
  }, [location]);

  return null;
}

function GlobalToaster() {
  const location = useLocation();
  const landingRoute = isLandingPath(location.pathname);
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Toaster
      position={!landingRoute && isMobile ? 'top-center' : 'bottom-right'}
      containerStyle={
        landingRoute
          ? undefined
          : {
            bottom: isMobile ? 70 : 80,
          }
      }
      toastOptions={{
        duration: 5000,
        style: {
          background: '#363636',
          color: '#fff',
        },
      }}
    />
  );
}

function GlobalOutageBanner() {
  const { pathname } = useLocation();

  return config.SHOW_OUTAGE_BANNER && !isLandingPath(pathname)
    ? <OutageBanner />
    : null;
}

function App() {
  // Initialize theme from localStorage
  React.useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'dark';
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);



  return (
    <div className="App-container">
      {/* Adopt the template description so route metadata replaces it during prerendering. */}
      <Helmet>
        <meta name="description" content="Turn notes and PDFs into practice quizzes fast. Quizzify uses active recall, spaced repetition, and gamified study to help you remember more." />
      </Helmet>
      <PWAInstallProvider>
        <Router>
          <GlobalOutageBanner />
          <GlobalToaster />
          <GA4RouteTracker />
          <Suspense fallback={<div className="loading-screen"><LoadingSpinner /></div>}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="*" element={<AppWithProviders />} />
            </Routes>
          </Suspense>
        </Router>
      </PWAInstallProvider>
    </div>
  );
}

export default App;
